import React, { useState } from 'react';
import { 
  ClipboardDocumentIcon, 
  HeartIcon,
  ShareIcon,
  StarIcon,
  EyeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { Prompt } from '../../types';
import Card from '../common/Card';
import Badge from '../common/Badge';
import Button from '../common/Button';
import PromptPersonalization from './PromptPersonalization';
import PersonalizedPromptView from './PersonalizedPromptView';

interface PromptDetailProps {
  prompt: Prompt | null;
  onClose: () => void;
  onCopy: (content: string) => void;
  onToggleFavorite?: (promptId: number) => void;
  onRate?: (promptId: number, rating: number) => void;
  isFavorite?: boolean;
  isOpen: boolean;
}

const PromptDetail: React.FC<PromptDetailProps> = ({
  prompt,
  onClose,
  onCopy,
  onToggleFavorite,
  onRate,
  isFavorite = false,
  isOpen,
}) => {
  const [showPersonalization, setShowPersonalization] = useState(false);
  const [rating, setRating] = useState(0);

  if (!isOpen || !prompt) {
    return null;
  }

  const handleCopy = (content: string) => {
    onCopy(content);
  };

  const handleToggleFavorite = () => {
    onToggleFavorite?.(prompt.id);
  };

  const handleRate = (newRating: number) => {
    setRating(newRating);
    onRate?.(prompt.id, newRating);
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: prompt.title,
        text: `${prompt.title} - EasyGPT 프롬프트`,
        url: window.location.href,
      });
    } catch (err) {
      // 공유 기능이 지원되지 않는 경우 URL 복사
      await navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* 배경 오버레이 */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        
        {/* 모달 컨텐츠 */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* 헤더 */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold text-gray-900 truncate">
                프롬프트 상세보기
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {prompt.chapterTitle}
              </p>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
            >
              <XMarkIcon className="w-5 h-5" />
            </Button>
          </div>

          {/* 컨텐츠 */}
          <div className="flex-1 overflow-y-auto max-h-[70vh]">
            <div className="p-6">
              <PersonalizedPromptView
                prompt={{
                  id: prompt.id,
                  title: prompt.title,
                  content: prompt.content,
                  chapterTitle: prompt.chapterTitle || '',
                  tags: prompt.tags || []
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptDetail;